import * as React from 'react';
import { useEffect, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid2';
import OutlinedInput from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import { useForm, Controller } from 'react-hook-form';
import Button from '@mui/material/Button'; 
import { Alert, FormHelperText, RadioGroup } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as consts from '../constants';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
  Box, Typography,
} from '@mui/material';
import { buildAddressesFromForm, parseAddress } from '../utils/addressUtils';
import { CALIFORNIA_CITIES } from '../utils/californiaCities';
import { IOrderInfo } from '../slices/authSlice';
import SafetyNotice from "./SafetyNotice";
import { shouldShowSafetyNotice } from "../utils/safetyNotice";
import {
  showNotification,
  NotificationType,
} from "../slices/notificationSlice";
import { useAppDispatch } from "../hooks/redux-hooks";
import ContactMethodFields from "./ContactMethodFields";

// Configure dayjs to use timezone
dayjs.extend(utc);
dayjs.extend(timezone);

export interface OrderSettings {
  service_type: 1;
  customer_name: string;
  email: string;
  phone?: string;
  wechat_id?: string;
  line_id?: string;
  addr1: string;
  addr1_street: string;
  addr1_zipcode: string;
  addr1_city: string;
  // addr1_state: string;
  addr2: string;
  addr2_street: string;
  addr2_zipcode: string;
  addr2_city: string;
  // addr2_state: string;
  addr3: string;
  addr3_street: string;
  addr3_zipcode: string;
  addr3_city: string;
  // addr3_state: string;
  passengers: number;
  luggages: number;
  carry_on: number;
  // other: string;
  airport: string;
  flight_number: string;
  lobby_service: boolean;
  flightDate: dayjs.Dayjs;
  flightTime: dayjs.Dayjs;
  language: string;
  contact_method: string;
  notes: string;
  qrcodeFile?: File | SerializedQrCodeFile;
  use_time: string;
  flight_time: string;
}  

type SerializedQrCodeFile = {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
};

const FormGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

interface PickupFormProps {
  parentSubmitCallback: (success: boolean, data: any) => void;
  submitRequested: boolean;
  initialValues?: any; 
  basicUserInfo?: any;
  futureOrders?: IOrderInfo[];
  isEdit?: boolean;
  onEditOrder?: (orderId: number) => void;
  // onSafetyNoticeChange?: (hasSafetyNotice: boolean) => void;
}

export default function PickupForm(props:PickupFormProps) {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<any[]>([]);

  const [multipleLocations, setMultipleLocations] = useState(() => {
    return props.initialValues && (props.initialValues.addr2 || props.initialValues.addr3) ? true : false;
  });
  const [addressCount, setAddressCount] = useState(() => {
    if (props.initialValues) {
      if (props.initialValues.addr3) return 3;
      if (props.initialValues.addr2) return 2;
    }
    return 1;
  });
  const addAddressClick = () => {
    if (addressCount < 3) {
      setAddressCount(addressCount + 1);
    }
  }

  const [flightTimeError, setFlightTimeError] = useState<string | null>(null);

  const validateStreetRequired = (value?: string) => {
    return value?.trim() ? true : t("errDropOffLocationRequired");
  };

  const getTrimmedFieldValue = (value: unknown) => {
    return typeof value === "string" ? value.trim() : "";
  };

  const getOptionalAddressGroupValues = (
    index: 2 | 3,
    overrides: Partial<Record<"street" | "city" | "zipcode", string>> = {}
  ) => {
    return {
      street: getTrimmedFieldValue(
        overrides.street ?? getValues(`addr${index}_street` as keyof OrderSettings)
      ),
      city: getTrimmedFieldValue(
        overrides.city ?? getValues(`addr${index}_city` as keyof OrderSettings)
      ),
      zipcode: getTrimmedFieldValue(
        overrides.zipcode ?? getValues(`addr${index}_zipcode` as keyof OrderSettings)
      ),
    };
  };

  const isOptionalAddressGroupEmpty = (group: {
    street: string;
    city: string;
    zipcode: string;
  }) => {
    return !group.street && !group.city && !group.zipcode;
  };

  const shouldRequireOptionalAddressGroup = (
    index: 2 | 3,
    group = getOptionalAddressGroupValues(index)
  ) => {
    if (addressCount < index) {
      return false;
    }

    if (index === 2 && addressCount === 3) {
      return true;
    }

    return !isOptionalAddressGroupEmpty(group);
  };

  const validateOptionalAddressField = (
    index: 2 | 3,
    field: "street" | "city" | "zipcode",
    value?: string
  ) => {
    const group = getOptionalAddressGroupValues(index, { [field]: value ?? "" });

    if (!shouldRequireOptionalAddressGroup(index, group)) {
      return true;
    }

    const errorMessages = {
      street: t("errDropOffLocationRequired"),
      city: t("errCityRequired"),
      zipcode: t("errZipCodeRequired"),
    };

    return group[field] ? true : errorMessages[field];
  };

  const onSubmit = (d: any) => {
    setFormData([...formData, d]);

    setFlightTimeError(null);
    const data = getValues() as Record<string, any>;
    if (data.contact_method === consts.contactMethod.PHONE) {
      delete data.wechat_id;
      delete data.line_id;
    } else if (data.contact_method === consts.contactMethod.WECHAT) {
      delete data.line_id;
    } else if (data.contact_method === consts.contactMethod.LINE) {
      delete data.wechat_id;
    }
    //data.flight_time = data.flightDate.format('YYYY-MM-DD') + ' ' + data.flightTime.format('HH:mm');
    // Ensure we're using PST timezone for flight time
    // Check if the object is a dayjs object with tz function
    const pstDate = dayjs.isDayjs(data.flightDate) ? 
      data.flightDate.tz('America/Los_Angeles') : 
      dayjs(data.flightDate).tz('America/Los_Angeles');
    
    const pstTime = dayjs.isDayjs(data.flightTime) ? 
      data.flightTime.tz('America/Los_Angeles') : 
      dayjs(data.flightTime).tz('America/Los_Angeles');

    data.flight_time = pstDate.format('YYYY-MM-DD') + ' ' + pstTime.format('HH:mm');

    const tmpFlightDateTime = dayjs(data.flight_time, "YYYY-MM-DD HH:mm")
    .tz("America/Los_Angeles", true);  // 确保解析为太平洋时间
    //.tz("Asia/Shanghai");  // 转换为北京时间

    const now = dayjs().tz("America/Los_Angeles");
    let hasError = false;
    // 验证使用时间必须大于当前时间
    if (tmpFlightDateTime.isBefore(now)) {
      setFlightTimeError(t("errUseTimeBeforeNow"));
      dispatch(
        showNotification({
          message: t("errUseTimeBeforeNow"),
          type: NotificationType.Error,
        })
      );
      hasError = true;
    }

    const { updated, isAddr1CityEmpty, omittedAddressIndexes } = buildAddressesFromForm({
      data,
      addressCount,
      defaultState: consts.defaultState,
    });
    // console.log('updated:', isAddr1CityEmpty, updated);
    Object.assign(data, updated);
    omittedAddressIndexes.forEach((index) => {
      delete data[`addr${index}`];
      delete data[`addr${index}_street`];
      delete data[`addr${index}_city`];
      delete data[`addr${index}_zipcode`];
    });
    // console.log('data:', data);
    if (isAddr1CityEmpty) {
      dispatch(
        showNotification({
          message: t("errCityRequired"),
          type: NotificationType.Error,
        })
      );
      hasError = true;
    }

    if (shouldShowSafetyNotice(Number(data.passengers ?? 0), Number(data.luggages ?? 0))) {
      dispatch(
        showNotification({
          message: t("safetyComplianceNoticeDesc"),
          type: NotificationType.Error,
        })
      );
      hasError = true;
    }

   // props.parentSubmitCallback(true, data);
    if (!hasError) {
      props.parentSubmitCallback(true, data);
    } else { 
      props.parentSubmitCallback(false, getValues());
    }
  };

  const onError = (e: any) => {
    console.error(e);
    props.parentSubmitCallback(false, getValues());
  };

  useEffect(() => {
    if (props.submitRequested) {
      handleSubmit(onSubmit, onError)();
      // props.parentSubmitCallback(getValues());
    }
  }, [props.submitRequested, props.parentSubmitCallback]);

  const {
    getValues,
    register,
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<OrderSettings>(
    {
      defaultValues: props.initialValues || {
        flightDate: dayjs().tz('America/Los_Angeles'),
        flightTime: dayjs().tz('America/Los_Angeles'),
        language: consts.languages.CHINESE,
        contact_method: consts.contactMethod.PHONE,
        lobby_service: false,
        passengers: 1,
        luggages: 1,
        carry_on: 1,
      }
    }
  )

  useEffect(() => {
    if (props.basicUserInfo && !getValues("customer_name")) {
      setValue("customer_name", props.basicUserInfo.name || "");
      setValue("email", props.basicUserInfo.email || "");
      setValue("phone", props.basicUserInfo.phone || "");
    }
  }, [props.basicUserInfo, setValue, getValues]);

  // const onSubmit: SubmitHandler<OrderSettings> = (data) => console.log(data)

  // console.log(watch("customer_name")) // watch input value by passing the name of it

  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [currentAddressIndex, setCurrentAddressIndex] = useState<number>(1);
  
  const handleOpenAddressDialog = (addressIndex: number) => {
    setCurrentAddressIndex(addressIndex);
    setOpenAddressDialog(true);
  };
  
  const handleSelectAddress = (address: string) => {
    const addressParts = parseAddress(address);
    const prefix = `addr${currentAddressIndex}_`;
    setValue(`${prefix}street` as keyof OrderSettings, addressParts.street);
    setValue(`${prefix}zipcode` as keyof OrderSettings, addressParts.zipcode);
    setValue(`${prefix}city` as keyof OrderSettings, addressParts.city);
    setValue(`${prefix}state` as keyof OrderSettings, addressParts.state);
    setOpenAddressDialog(false);
  };

  const [showOrderWarning, setShowOrderWarning] = useState(false);
  const [existingOrder, setExistingOrder] = useState<IOrderInfo | null>(null);
  const watchedFlightDate = watch("flightDate");
  const watchedPhone = watch("phone");
  const watchedAirport = watch("airport");
  const watchedPassengers = Number(watch("passengers") ?? 0);
  const watchedLuggages = Number(watch("luggages") ?? 0);
  const watchedContactMethod = watch("contact_method");
  const watchedQrCodeFile = watch("qrcodeFile");
  const [qrCodeFileName, setQrCodeFileName] = useState(() => {
    const initialFile = props.initialValues?.qrcodeFile as
      | File
      | SerializedQrCodeFile
      | undefined;
    return initialFile?.name || "";
  });

  const normalizePhone = (phone?: string) => (phone || "").replace(/\D/g, "");
  const normalizeAirport = (airport?: string) => (airport || "").trim().toLowerCase();

  // 检查选择的日期是否已有订单
  const checkExistingOrder = (selectedDate: any, phone?: string, airport?: string) => {
    if (!props.futureOrders || props.isEdit) return null;
    if (!selectedDate || !phone || !airport) return null;

    const normalizedPhone = normalizePhone(phone);
    const normalizedAirport = normalizeAirport(airport);

    const existingOrder = props.futureOrders.find(order => 
      order.service_type !== undefined &&
      order.service_type === consts.serviceType.AIRPORT_PICK_UP &&
      //dayjs(order.flight_time).format('YYYY-MM-DD') === dayjs(selectedDate).format('YYYY-MM-DD')
      dayjs(order.flight_time).tz('America/Los_Angeles').format('YYYY-MM-DD') === 
      dayjs(selectedDate).tz('America/Los_Angeles').format('YYYY-MM-DD') &&
      normalizePhone(order.phone) === normalizedPhone &&
      normalizeAirport(order.airport) === normalizedAirport
    );

    return existingOrder;
  };

  useEffect(() => {
    const existing = checkExistingOrder(watchedFlightDate, watchedPhone, watchedAirport);
    if (existing) {
      setExistingOrder(existing);
      setShowOrderWarning(true);
      return;
    }
    setExistingOrder(null);
    setShowOrderWarning(false);
  }, [watchedFlightDate, watchedPhone, watchedAirport, props.futureOrders, props.isEdit]);

  // useEffect(() => {
  //   const hasSafetyNotice = shouldShowSafetyNotice(watchedPassengers, watchedLuggages);
  //   props.onSafetyNoticeChange?.(hasSafetyNotice);
  // }, [watchedPassengers, watchedLuggages, props.onSafetyNoticeChange]);

  const handleQrCodeFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      dispatch(
        showNotification({
          message: t("errQrcodeFileInvalidType"),
          type: NotificationType.Error,
        })
      );
      event.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      dispatch(
        showNotification({
          message: t("errQrcodeFileTooLarge"),
          type: NotificationType.Error,
        })
      );
      event.target.value = "";
      return;
    }

    setValue("qrcodeFile", file, { shouldDirty: true });
    setQrCodeFileName(file.name);
  };

  const { t } = useTranslation();
  return (
    <Grid container spacing={3}>
      <FormGrid size={{ xs: 12 }}>
        <Typography fontWeight="medium" gutterBottom>
          {t("pickupService")}
        </Typography>
      </FormGrid>
      <FormGrid size={{ xs: 6 }}>
        <FormLabel id="customer_name_label" required>
          {t("name")}
        </FormLabel>
        <Controller
          name="customer_name"
          control={control}
          rules={{ 
            required: { value: true, message: t("errNameRequired") },
            minLength: { value: 2, message: t("errNameTooShort") },
            maxLength: { value: 30, message: t("errNameTooLong") }
          }}
          defaultValue=''
          render={({ field }) => <TextField 
            aria-labelledby="customer_name_label"
            error={!!errors.customer_name} 
            helperText={errors.customer_name ? errors.customer_name.message : ''}
            {...field} 
          />}
        />
      </FormGrid>
      <FormGrid size={{ xs: 6 }}>
        <FormLabel id="booking_for">{t("bookingFor")}</FormLabel>
        <RadioGroup
          row
          defaultValue="self"
          onChange={(e) => {
            if (e.target.value === "self" && props.basicUserInfo) {
              setValue("customer_name", props.basicUserInfo.name || "");
              setValue("email", props.basicUserInfo.email || "");
              setValue("phone", props.basicUserInfo.phone || "");
            } else {
              setValue("customer_name", "");
              setValue("email", "");
              setValue("phone", "");
            }
          }}
        >
          <FormControlLabel
            value="self"
            control={<Radio />}
            label={t("bookForMyself")}
          />
          <FormControlLabel
            value="others"
            control={<Radio />}
            label={t("bookForOthers")}
          />
        </RadioGroup>
      </FormGrid>
      <ContactMethodFields
        control={control as any}
        errors={errors as any}
        watchedContactMethod={watchedContactMethod}
      />
      <FormGrid
        size={
          watchedContactMethod === consts.contactMethod.PHONE
            ? { xs: 12, md: 6 }
            : { xs: 12, md: 12 }
        }
      >
        <FormLabel id="phone" required>
          {t("phone")}
        </FormLabel>
        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (v) => {
              if (!v?.trim()) return t("errPhoneRequired");
              return /^\+?[0-9]{9,15}$/.test(v ?? "") || t("errPhoneInvalid");
            },
          }}
          render={({ field }) => (
            <TextField
              aria-labelledby="phone"
              error={!!errors.phone}
              helperText={errors.phone ? String(errors.phone.message ?? "") : ""}
              placeholder={t("contactDetailsPhonePlaceholder")}
              {...field}
            />
          )}
        />
      </FormGrid>

      <FormGrid size={{ xs: 12, md: 12 }}>
        <FormLabel id="email" required>
          {t("emailAddress")}
        </FormLabel>
        <Controller
          name="email"
          control={control}
          rules={{ 
            validate: {
              matchPattern: (v) =>
                /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
                t("errEmailInvalid")
            },
            required: { value: true, message: t("errEmailRequired") } 
          }}
          render={({ field }) => <TextField 
          aria-labelledby="email"
          error={!!errors.email} 
          helperText={errors.email ? errors.email.message : ''}
          {...field} 
        />}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 4 }}>
        <FormLabel id="passengers" required>
          {t("passengers")}
        </FormLabel>
        <Controller
          name="passengers"
          control={control}
          rules={{ min: 0, required: { value: true, message: t("errPassengersRequired") } }}
          render={({ field }) => (
            <Select
              {...field}
            >
              {consts.passengersFields.map((num, index) => (
                <MenuItem key={'passenger_'+index} value={num}>{num}</MenuItem>
              ))}
            </Select>
          )}
        />
        <FormHelperText error={true}>{errors.passengers?.message}</FormHelperText>
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 4 }}>
        <FormLabel id="luggages" required>
          {t("luggages")}
        </FormLabel>
        <Controller
          name="luggages"
          control={control}
          rules={{ min: 0, required: { value: true, message: t("errLuggagesRequired") } }}
          render={({ field }) => (
            <Select
              {...field}
            >
              {consts.luggagesFields.map((num, index) => (
                <MenuItem key={'luggages_'+index} value={num}>{num}</MenuItem>
              ))}
            </Select>
          )}
        />
        <FormHelperText error={true}>{errors.luggages?.message}</FormHelperText>
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 4 }}>
        <FormLabel id="carry_on" required>
          {t("carryOns")}
        </FormLabel>
        <Controller
          name="carry_on"
          control={control}
          rules={{ 
            validate : {min: v=> v >= 0 || t("errCarryOnsRequired")},
            //required: { value: true, message: t("errCarryOnsRequired") } 
          }}
          render={({ field }) => (
            <Select
              {...field}
            >
              {consts.carryOnFields.map((num, index) => (
                <MenuItem key={'carry_'+index} value={num}>{num}</MenuItem>
              ))}
            </Select>
          )}
        />
        <FormHelperText error={true}>{errors.carry_on?.message}</FormHelperText>
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <SafetyNotice passengers={watchedPassengers} luggage={watchedLuggages} />
      </FormGrid>
      {/* <FormGrid size={{ xs: 12, md: 3 }}>
        <FormLabel id="other">{t("others")}</FormLabel>
        <Controller
          name="other"
          control={control}
          render={({ field }) => <OutlinedInput {...field} />}
        />
      </FormGrid> */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel id="flightDate" required>
          {t("flightArrivalDate")}
        </FormLabel>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Controller
            name="flightDate"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <DatePicker
                  value={value}
                  minDate={dayjs().tz('America/Los_Angeles')}
                  onChange={(date) => {
                    // Convert to PST timezone
                    const pstDate = date ? dayjs(date).tz('America/Los_Angeles') : null;
                    onChange(pstDate);//date);
                    setFlightTimeError(null);
                  }}
                />
            )} 
          />
        </LocalizationProvider>
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel id="flightTime" required>
          {t("flightArrivalTime")}
        </FormLabel>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Controller
            name="flightTime"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TimePicker
                  value={value}
                  ampm={false} // 使用24小时制
                  // timezone="America/Los_Angeles"
                  // onChange={(event) => { onChange(event); }}
                  onChange={(event) => { 
                    // Convert to PST timezone
                    const pstTime = event ? dayjs(event).tz('America/Los_Angeles') : null;
                    onChange(pstTime); 
                    setFlightTimeError(null);
                  }}
                  slotProps={{
                    textField: {
                      error: !!flightTimeError,
                      helperText: flightTimeError
                    }
                  }}
                />
            )} 
          />
        </LocalizationProvider>
      </FormGrid>
      <FormGrid size={{ xs: 6 }}>
        <FormLabel id="flight_number">{t("airlineIncAndNumber")}</FormLabel>
        <Controller
          name="flight_number"
          control={control}
          rules={{ required: { value: true, message: t("errFlightNumberRequired") } }}
          render={({ field }) => <OutlinedInput
          placeholder="AA 1718"
          {...field}
          error={!!errors.flight_number}
          />}
        />
        <FormHelperText error={true}>{errors.flight_number?.message}</FormHelperText>
      </FormGrid>
      <FormGrid size={{ xs: 6 }}>
        <FormLabel id="lobby_service" sx={{ whiteSpace: "pre-line" }}>
          {t("lobbyServicePickupDetail")}
        </FormLabel>
        <Controller
          name="lobby_service"
          control={control}
          rules={{ 
            validate: value => value !== undefined && value !== null || t("errLobbyServiceRequired") 
          }}
          render={({
            field: { onChange, value },
            fieldState: { error },
            formState,
          }) => (
            <RadioGroup 
              row 
              value={value ? "true" : "false"} 
              onChange={(e) => onChange(e.target.value === "true")}
            >
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={t("yes")}
              />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={t("no")}
              />
            </RadioGroup>
          )}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 8}}>
      <RadioGroup
        row
        aria-labelledby="demo-radio-buttons-group-label"
        defaultValue="single"
        name="radio-buttons-group"
      >
        <FormControlLabel 
          value="single" 
          control={<Radio />} 
          label={t("rdoSingleAddr")}
          checked={!multipleLocations}
          onChange={() => {
            if (multipleLocations) {
              setMultipleLocations(false);
              setAddressCount(1);
            } else {
              setMultipleLocations(true);
            }
          }}
        />
        <FormControlLabel 
          value="multiple" 
          control={<Radio />} 
          label={t("rdoMultiAddr")}
          checked={multipleLocations}
          onChange={() => {setMultipleLocations(true);}
          }
        />
      </RadioGroup>
      </FormGrid>
      <FormGrid size={{ xs: 12, md: 4}}>
      {multipleLocations && (
        <Button variant="contained" onClick={addAddressClick}>{t("btnAddAddress")}</Button>
      )}
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <FormLabel id="addr1" required>{t("dropOffLocation")}</FormLabel>
        <Button 
          variant="contained" 
          size="small"
          onClick={() => handleOpenAddressDialog(1)}
        >
          {t("btnFromMyAddress")}
        </Button>
      </Box>
        <Grid container spacing={2}>
          <FormGrid size={{ xs: 12, md: 12 }}>
            <Controller
              name="addr1_street"
              control={control}
              rules={{ validate: validateStreetRequired }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                placeholder={t("streetAddress")}
                error={!!errors.addr1_street}
                helperText={errors.addr1_street?.message}
              />}
            />
          </FormGrid>
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr1_city"
              control={control}
              rules={{ required: { value: true, message: t("errCityRequired") } }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={CALIFORNIA_CITIES}
                  fullWidth
                  value={field.value || ""}
                  onInputChange={(_, newInputValue) => {
                    field.onChange(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("city")}
                      error={!!errors.addr1_city}
                      helperText={errors.addr1_city?.message}
                    />
                  )}
                />
              )}
            />
          </FormGrid>
          {/* <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr1_state"
              control={control}
              rules={{ required: { value: true, message: t("errStateRequired") } }}
              render={({ field }) => (
                <Autocomplete
                {...field}
                options={consts.states}
                fullWidth
                value={field.value ? consts.states.find(state => state.startsWith(field.value)) || null : null}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.split(' - ')[0] : null);
                }}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("selState")}
                    error={!!errors.addr1_state}
                    helperText={errors.addr1_state?.message}
                  />
                )}
              />
              )}
            />
          </FormGrid> */}
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr1_zipcode"
              control={control}
              rules={{ 
                validate: {
                  matchPattern: (v) =>
                    /^\d{2,8}$/.test(v) || t("errZipcodeInvalid")
                },
                required: { value: true, message: t("errZipCodeRequired") }
              }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                type="text"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                placeholder={t("zipcode")}
                error={!!errors.addr1_zipcode}
                helperText={errors.addr1_zipcode?.message}
              />}
            />
          </FormGrid>
        </Grid>
      </FormGrid>
      {addressCount >= 2 && (
      <FormGrid size={{ xs: 12 }}>
        {/* <FormLabel id="addr2" required>{t("dropOffLocation")  + ' 2'}</FormLabel> */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <FormLabel id="addr2" required>{t("dropOffLocation") + ' 2'}</FormLabel>
          <Button 
            variant="contained" 
            size="small"
            onClick={() => handleOpenAddressDialog(2)}
          >
            {t("btnFromMyAddress")}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <FormGrid size={{ xs: 12, md: 12 }}>
            <Controller
              name="addr2_street"
              control={control}
              rules={{
                validate: (value) => validateOptionalAddressField(2, "street", value),
              }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                placeholder={t("streetAddress")}
                error={!!errors.addr2_street}
                helperText={errors.addr2_street?.message}
              />}
            />
          </FormGrid>
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr2_city"
              control={control}
              rules={{ 
                validate: (value) => validateOptionalAddressField(2, "city", value),
              }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={CALIFORNIA_CITIES}
                  fullWidth
                  value={field.value || ""}
                  onInputChange={(_, newInputValue) => {
                    field.onChange(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("city")}
                      error={!!errors.addr2_city}
                      helperText={errors.addr2_city?.message}
                    />
                  )}
                />
              )}
            />
          </FormGrid>
          {/* <FormGrid size={{ xs: 6, md: 6 }}>
          <Controller
              name="addr2_state"
              control={control}
              render={({ field }) => (
                <Autocomplete
                {...field}
                options={consts.states}
                fullWidth
                value={field.value ? consts.states.find(state => state.startsWith(field.value)) || null : null}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.split(' - ')[0] : null);
                }}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("selState")}
                    error={!!errors.addr2_state}
                    helperText={errors.addr2_state?.message}
                  />
                )}
              />
              )}
            />
          </FormGrid> */}
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr2_zipcode"
              control={control}
              rules={{ 
                validate: (value) => validateOptionalAddressField(2, "zipcode", value),
              }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                placeholder={t("zipcode")}
                error={!!errors.addr2_zipcode}
                helperText={errors.addr2_zipcode?.message}
              />}
            />
          </FormGrid>
        </Grid>
      </FormGrid>
      )}
      {addressCount === 3 && (
      <FormGrid size={{ xs: 12 }}>
        {/* <FormLabel id="addr3" required>{t("dropOffLocation")   + ' 3'}</FormLabel> */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <FormLabel id="addr3" required>{t("dropOffLocation") + ' 3'}</FormLabel>
          <Button 
            variant="contained" 
            size="small"
            onClick={() => handleOpenAddressDialog(3)}
          >
            {t("btnFromMyAddress")}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <FormGrid size={{ xs: 12, md: 12 }}>
            <Controller
              name="addr3_street"
              control={control}
              rules={{
                validate: (value) => validateOptionalAddressField(3, "street", value),
              }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                placeholder={t("streetAddress")}
                error={!!errors.addr3_street}
                helperText={errors.addr3_street?.message}
              />}
            />
          </FormGrid>
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr3_city"
              control={control}
              rules={{ 
                validate: (value) => validateOptionalAddressField(3, "city", value),
              }}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  options={CALIFORNIA_CITIES}
                  fullWidth
                  value={field.value || ""}
                  onInputChange={(_, newInputValue) => {
                    field.onChange(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("city")}
                      error={!!errors.addr3_city}
                      helperText={errors.addr3_city?.message}
                    />
                  )}
                />
              )}
            />
          </FormGrid>
          <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr3_zipcode"
              control={control}
              rules={{ 
                validate: (value) => validateOptionalAddressField(3, "zipcode", value),
              }}
              render={({ field }) => <TextField 
                {...field}
                fullWidth
                placeholder={t("zipcode")}
                error={!!errors.addr3_zipcode}
                helperText={errors.addr3_zipcode?.message}
              />}
            />
          </FormGrid>
          {/* <FormGrid size={{ xs: 6, md: 6 }}>
            <Controller
              name="addr3_state"
              control={control}
              render={({ field }) => (
                <Autocomplete
                {...field}
                options={consts.states}
                fullWidth
                value={field.value ? consts.states.find(state => state.startsWith(field.value)) || null : null}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.split(' - ')[0] : null);
                }}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("selState")}
                    error={!!errors.addr3_state}
                    helperText={errors.addr3_state?.message}
                  />
                )}
              />
              )}
            />
          </FormGrid> */}
        </Grid>
      </FormGrid>
      )}
      <FormGrid size={{ xs: 12 }}>
        <FormLabel id="airport" required>{t("pickupAirport")}</FormLabel>
        <Controller
          name="airport"
          control={control}
          rules={{ minLength: 1, required: { value: true, message: t("errAirportRequired") } }}
          render={({ field }) => (
            <Select
              {...field}
            >
              {consts.airports.map((airport, index) => (
                <MenuItem key={'air_'+index} value={airport}>{airport}</MenuItem>
              ))}
            </Select>
          )}
        />
        <FormHelperText error={true}>{errors.airport?.message}</FormHelperText>
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <FormLabel id="language">{t("preferredServiceLanguage")}</FormLabel>
        <Controller
          name="language"
          control={control}
          rules={{ required: true }}
          render={({
            field: { onChange, value },
            fieldState: { error },
            formState,
          }) => (
            <RadioGroup row value={value} onChange={onChange}>
              <FormControlLabel
                value={consts.languages.ENGLISH}
                control={<Radio />}
                label={t("english")}
              />
              <FormControlLabel
                value={consts.languages.CHINESE}
                control={<Radio />}
                label={t("mandarin")}
              />
            </RadioGroup>
          )}
        />
      </FormGrid>
      <FormGrid size={{ xs: 12 }}>
        <FormLabel id="note_label">{t("note")}</FormLabel>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => <TextField 
          multiline 
          rows={4} 
          variant="filled"
          placeholder={t("notePlaceholder")}
          {...field} 
          />}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button component="label" variant="contained">
            {t("btnUploadGateCode")}
            <input
              hidden
              type="file"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              onChange={handleQrCodeFileChange}
            />
          </Button>
        </Box>
        {watchedQrCodeFile && (
          <FormHelperText sx={{ mt: 1 }}>
            {t("msgGateCodeUploaded")}
            {qrCodeFileName ? ` (${qrCodeFileName})` : ""}
          </FormHelperText>
        )}
      </FormGrid>
      <Dialog 
        open={openAddressDialog} 
        onClose={() => setOpenAddressDialog(false)}
        PaperProps={{
          sx: {
            width: '400px',
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          {t("titleSelectFromMyAddress")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <List>
            {[...Array(5)].map((_, index) => {
              const addrKey = `addr${index + 1}` as keyof typeof props.basicUserInfo;
              const address = props.basicUserInfo?.[addrKey];
              
              if (address) {
                return (
                  <ListItem 
                    key={`address_${index + 1}`}
                    onClick={() => handleSelectAddress(address)}
                    sx={{ 
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={t(`addr`) + `${index + 1}`}
                      secondary={address}
                      primaryTypographyProps={{
                        fontWeight: 'medium'
                      }}
                    />
                  </ListItem>
                );
              }
              return null;
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setOpenAddressDialog(false)}
            variant="contained"
            color="primary"
          >
            {t("btnCancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showOrderWarning}
        onClose={() => setShowOrderWarning(false)}
        PaperProps={{
          sx: {
            width: '400px',
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          {t("existingOrderWarning")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("existingOrderMessage")}
          </Typography>
          {existingOrder && (
            <Box sx={{ 
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="subtitle2">{t("orderInfo")}</Typography>
              <Typography variant="body2">
                  {t("name")}: {existingOrder.customer_name}
                </Typography>
              <Typography variant="body2">
                {`${t("flightTime")}: ${dayjs(existingOrder.flight_time).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm')}`}
              </Typography>
              <Typography variant="body2">
                {`${t("airport")}: ${existingOrder.airport}`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setShowOrderWarning(false)}
          >
            {t("continueNew")}
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => {
              if (existingOrder && props.onEditOrder) {
                props.onEditOrder(existingOrder.order_id);
              }
            }}
          >
            {t("editExisting")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
